// Input Flows v2 (PRD §4.4 / WP-09).
// Keyword-triggered Q&A surveys collecting structured answers into subscriber custom fields,
// auto-syncing to Google Sheets (WP-05) and dispatching input_flow.completed webhooks.
import { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useI18n } from '../lib/i18n';
import Modal from '../components/ui/Modal';
import Toggle from '../components/ui/Toggle';
import Card from '../components/ui/Card';
import StatCard from '../components/ui/StatCard';
import Badge from '../components/ui/Badge';
import Button from '../components/ui/Button';
import EmptyState from '../components/ui/EmptyState';
import { useToast } from '../components/ui/Toast';
import { useConfirm } from '../components/ui/ConfirmDialog';
import {
  useInputFlows,
  saveInputFlow,
  deleteInputFlow,
  type InputFlow,
  type FlowInput,
  type FlowQuestion,
} from '../lib/inputFlows';
import {
  googleAccountsList,
  googleSheetsList,
  type GoogleAccountDoc,
  type SheetItem,
} from '../lib/google';
import {
  CheckIcon,
  PlusIcon,
  ExternalLinkIcon,
  SearchIcon,
  TrashIcon,
  InboxIcon,
} from '../components/icons';

function newQ(): FlowQuestion {
  const id =
    globalThis.crypto?.randomUUID?.() ??
    `q_${Date.now()}_${Math.random().toString(36).slice(2)}`;
  return { id, prompt: '', fieldKey: '' };
}

const emptyFlow = (): FlowInput => ({
  name: '',
  trigger: '',
  enabled: true,
  questions: [newQ()],
  completion: "Thank you! We've recorded your responses.",
  googleAccountId: null,
  googleSheetId: null,
});

export default function InputFlows() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const toast = useToast();
  const confirm = useConfirm();
  const { flows, loading } = useInputFlows();

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'draft'>('all');
  const [sheetFilter, setSheetFilter] = useState<'all' | 'with_sheet' | 'no_sheet'>('all');

  const [editing, setEditing] = useState<FlowInput | null>(null);
  const [useGoogleSheet, setUseGoogleSheet] = useState(false);
  const [googleAccounts, setGoogleAccounts] = useState<GoogleAccountDoc[]>([]);
  const [googleSheets, setGoogleSheets] = useState<SheetItem[]>([]);
  const [loadingGoogle, setLoadingGoogle] = useState(false);
  const [manualSheetId, setManualSheetId] = useState(false);

  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // Load Google accounts on mount or when opening editor
  useEffect(() => {
    googleAccountsList()
      .then((res) => {
        if (res.ok && res.accounts) {
          setGoogleAccounts(res.accounts);
        }
      })
      .catch((e) => setErr(e.message || 'Failed to load Google accounts'));
  }, []);

  // When selected googleAccountId changes, load its sheets
  useEffect(() => {
    if (!editing?.googleAccountId) {
      setGoogleSheets([]);
      return;
    }
    setLoadingGoogle(true);
    googleSheetsList(editing.googleAccountId)
      .then((res) => {
        if (res.ok && res.sheets) {
          setGoogleSheets(res.sheets);
        }
      })
      .catch((e) => setErr(e.message || 'Failed to load Google sheets'))
      .finally(() => setLoadingGoogle(false));
  }, [editing?.googleAccountId]);

  // Summary stats
  const stats = useMemo(() => {
    const total = flows.length;
    const active = flows.filter((f) => f.enabled).length;
    const totalCompletions = flows.reduce((sum, f) => sum + (f.completions || 0), 0);
    const linkedSheets = flows.filter((f) => Boolean(f.googleSheetId)).length;
    return { total, active, totalCompletions, linkedSheets };
  }, [flows]);

  // Filtered flows
  const filteredFlows = useMemo(() => {
    return flows.filter((f) => {
      if (
        search &&
        !f.name.toLowerCase().includes(search.toLowerCase()) &&
        !f.trigger.toLowerCase().includes(search.toLowerCase())
      ) {
        return false;
      }
      if (statusFilter === 'active' && !f.enabled) return false;
      if (statusFilter === 'draft' && f.enabled) return false;
      if (sheetFilter === 'with_sheet' && !f.googleSheetId) return false;
      if (sheetFilter === 'no_sheet' && f.googleSheetId) return false;
      return true;
    });
  }, [flows, search, statusFilter, sheetFilter]);

  const openCreateModal = () => {
    const fresh = emptyFlow();
    setEditing(fresh);
    setUseGoogleSheet(false);
    setManualSheetId(false);
    setErr(null);
  };

  const openEditModal = (f: InputFlow) => {
    setEditing({
      id: f.id,
      name: f.name,
      trigger: f.trigger,
      enabled: f.enabled,
      questions: f.questions.length ? f.questions : [newQ()],
      completion: f.completion ?? '',
      googleAccountId: f.googleAccountId || null,
      googleSheetId: f.googleSheetId || null,
    });
    setUseGoogleSheet(Boolean(f.googleSheetId));
    setManualSheetId(false);
    setErr(null);
  };

  const patchQ = (i: number, patch: Partial<FlowQuestion>) => {
    if (!editing) return;
    setEditing({
      ...editing,
      questions: editing.questions.map((q, x) => (x === i ? { ...q, ...patch } : q)),
    });
  };

  const handleSave = async () => {
    if (!editing || !editing.name.trim() || !editing.trigger.trim()) return;
    const validQuestions = editing.questions.filter(
      (q) => q.prompt.trim() && q.fieldKey.trim()
    );
    if (!validQuestions.length) {
      setErr(t('flow.needQ'));
      return;
    }

    setBusy(true);
    setErr(null);

    const payload: FlowInput = {
      ...editing,
      name: editing.name.trim(),
      trigger: editing.trigger.trim().toLowerCase(),
      questions: validQuestions,
      completion: editing.completion?.trim() || null,
      googleAccountId: useGoogleSheet ? editing.googleAccountId || null : null,
      googleSheetId: useGoogleSheet ? editing.googleSheetId || null : null,
    };

    try {
      await saveInputFlow(payload);
      toast.success(editing.id ? t('flow.updatedSuccess') : t('flow.createdSuccess'));
      setEditing(null);
    } catch (e: any) {
      setErr(e.message || t('flow.saveFailed'));
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async (id: string, name: string) => {
    if (!(await confirm({ title: t('common.areYouSure'), body: t('flow.deleteConfirm', { name }), danger: true }))) return;
    try {
      await deleteInputFlow(id);
      toast.success(t('flow.deletedSuccess'));
    } catch (e: any) {
      toast.error(e.message || t('flow.deleteFailed'));
    }
  };

  return (
    <div className="space-y-6">
      {/* Top Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <div className="flex items-center gap-2 text-xs font-semibold text-ink-3 dark:text-slate-400">
            <button
              type="button"
              onClick={() => navigate('/bots')}
              className="hover:text-ink dark:hover:text-slate-200 transition"
            >
              {t('bot.title')}
            </button>
            <span>/</span>
            <span className="text-ink dark:text-slate-200">{t('flow.title')}</span>
          </div>
          <h1 className="text-xl font-extrabold text-ink dark:text-slate-100 mt-1">
            {t('flow.title')}
          </h1>
          <p className="text-xs text-ink-3 dark:text-slate-400">
            {t('flow.subtitle')}
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="secondary"
            size="sm"
            onClick={() => navigate('/flows')}
            className="text-xs"
          >
            <ExternalLinkIcon size={14} />
            <span>{t('flow.buildAsFlow')}</span>
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={openCreateModal}
            className="text-xs"
          >
            <PlusIcon size={14} />
            <span>{t('flow.add')}</span>
          </Button>
        </div>
      </div>

      {/* Visual Flow Builder Callout Banner */}
      <div className="p-3.5 rounded-2xl border border-brand/20 bg-brand/5 dark:bg-brand-950/20 dark:border-brand-900/40 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="flex items-start gap-3">
          <span className="text-xl shrink-0">🔀</span>
          <div>
            <h4 className="text-xs font-bold text-ink dark:text-slate-200">
              {t('flow.visualBuilderBannerTitle')}
            </h4>
            <p className="text-[11px] text-ink-3 dark:text-slate-400 mt-0.5">
              {t('flow.visualBuilderBannerDesc')}
            </p>
          </div>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate('/flows')}
          className="text-xs text-brand font-bold shrink-0 self-start sm:self-auto"
        >
          <span>{t('flow.openFlowBuilder')}</span> →
        </Button>
      </div>

      {/* Stat Cards */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <StatCard
          label={t('flow.statsTotal')}
          value={stats.total}
          icon={<InboxIcon size={18} className="text-blue-500" />}
        />
        <StatCard
          label={t('flow.statsActive')}
          value={stats.active}
          icon={<CheckIcon size={18} className="text-emerald-500" />}
        />
        <StatCard
          label={t('flow.statsCompletions')}
          value={stats.totalCompletions}
          icon={<CheckIcon size={18} className="text-brand" />}
        />
        <StatCard
          label={t('flow.statsSheets')}
          value={stats.linkedSheets}
          icon={<span className="text-base">📊</span>}
        />
      </div>

      {/* Filters Bar */}
      <Card className="p-3.5 flex flex-col sm:flex-row gap-3 sm:items-center justify-between">
        <div className="flex-1 relative min-w-[200px]">
          <SearchIcon
            size={14}
            className="absolute start-3 top-1/2 -translate-y-1/2 text-ink-4 dark:text-slate-500"
          />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('flow.searchPlaceholder')}
            className="w-full rounded-xl border border-line bg-surface ps-9 pe-3 py-1.5 text-xs text-ink outline-none focus:border-brand dark:border-slate-800 dark:bg-slate-800/80 dark:text-slate-100"
          />
        </div>

        <div className="flex flex-wrap items-center gap-2 text-xs">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as any)}
            className="rounded-xl border border-line bg-surface px-2.5 py-1.5 text-xs text-ink outline-none focus:border-brand dark:border-slate-800 dark:bg-slate-800 dark:text-slate-200"
          >
            <option value="all">{t('flow.allStatuses')}</option>
            <option value="active">{t('flow.statusActive')}</option>
            <option value="draft">{t('flow.statusDraft')}</option>
          </select>

          <select
            value={sheetFilter}
            onChange={(e) => setSheetFilter(e.target.value as any)}
            className="rounded-xl border border-line bg-surface px-2.5 py-1.5 text-xs text-ink outline-none focus:border-brand dark:border-slate-800 dark:bg-slate-800 dark:text-slate-200"
          >
            <option value="all">{t('flow.allSheetFilters')}</option>
            <option value="with_sheet">{t('flow.sheetLinked')}</option>
            <option value="no_sheet">{t('flow.sheetNone')}</option>
          </select>
        </div>
      </Card>

      {/* Flows Grid */}
      {loading ? (
        <div className="py-12 text-center text-xs text-ink-3 dark:text-slate-400">
          <span className="animate-spin inline-block me-2">⏳</span>
          {t('common.loading')}
        </div>
      ) : filteredFlows.length === 0 ? (
        <EmptyState
          title={t('flow.empty')}
          description={t('flow.emptyDesc')}
          action={
            <Button variant="primary" size="sm" onClick={openCreateModal}>
              <PlusIcon size={14} />
              <span>{t('flow.add')}</span>
            </Button>
          }
        />
      ) : (
        <div className="grid gap-3.5 sm:grid-cols-2 lg:grid-cols-3">
          {filteredFlows.map((f) => (
            <Card
              key={f.id}
              className="p-4 flex flex-col justify-between hover:shadow-md transition"
            >
              <div>
                {/* Header */}
                <div className="flex items-start justify-between gap-2 mb-2.5">
                  <div className="min-w-0">
                    <h3
                      className="text-sm font-bold text-ink dark:text-slate-100 truncate"
                      title={f.name}
                    >
                      {f.name}
                    </h3>
                    <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                      {f.enabled ? (
                        <Badge variant="success" size="sm">
                          {t('forms.live')}
                        </Badge>
                      ) : (
                        <Badge variant="neutral" size="sm">
                          {t('forms.off')}
                        </Badge>
                      )}
                      <span className="text-[11px] font-mono font-bold px-2 py-0.5 rounded-md bg-brand-50 text-brand dark:bg-brand-950/40 dark:text-brand-tint">
                        #{f.trigger}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Attributes */}
                <div className="space-y-1.5 py-2.5 border-y border-line dark:border-slate-800 text-xs text-ink-2 dark:text-slate-300">
                  <div className="flex justify-between items-center">
                    <span className="text-ink-3 dark:text-slate-400">
                      {t('flow.questions')}:
                    </span>
                    <span className="font-semibold">{f.questions.length}</span>
                  </div>

                  <div className="flex justify-between items-center">
                    <span className="text-ink-3 dark:text-slate-400">
                      {t('flow.done')}:
                    </span>
                    <span className="font-semibold text-emerald-600 dark:text-emerald-400">
                      {f.completions} {t('flow.completionsLabel')}
                    </span>
                  </div>

                  <div className="flex justify-between items-center">
                    <span className="text-ink-3 dark:text-slate-400">
                      {t('flow.googleSheet')}:
                    </span>
                    <span className="text-[11px] truncate max-w-[170px] font-mono">
                      {f.googleSheetId ? (
                        <span className="inline-flex items-center gap-1 text-emerald-600 dark:text-emerald-400 font-semibold">
                          <span>📊</span>
                          <span>{t('flow.sheetConnected')}</span>
                        </span>
                      ) : (
                        t('flow.sheetNotConnected')
                      )}
                    </span>
                  </div>

                  <div className="flex justify-between items-center text-[11px] text-ink-3 dark:text-slate-400">
                    <span>{t('flow.webhookEvent')}:</span>
                    <span className="font-mono text-[10px] bg-slate-100 dark:bg-slate-800 px-1.5 py-0.5 rounded">
                      input_flow.completed
                    </span>
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="mt-4 flex items-center justify-end gap-1.5 pt-2">
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => openEditModal(f)}
                  className="text-xs"
                >
                  {t('cat.edit')}
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleDelete(f.id, f.name)}
                  className="text-xs text-rose-600 hover:text-rose-700 hover:bg-rose-50 dark:hover:bg-rose-950/30"
                >
                  <TrashIcon size={13} />
                </Button>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Editor Modal */}
      <Modal
        open={editing !== null}
        onClose={() => setEditing(null)}
        title={editing?.id ? t('flow.editTitle') : t('flow.add')}
      >
        {editing && (
          <div className="space-y-4 max-h-[75vh] overflow-y-auto pe-1">
            {/* Basic Info */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-bold text-ink dark:text-slate-200 mb-1">
                  {t('flow.name')} <span className="text-rose-500">*</span>
                </label>
                <input
                  value={editing.name}
                  onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                  placeholder={t('flow.namePlaceholder')}
                  className="w-full rounded-xl border border-line bg-white px-3.5 py-2 text-sm text-ink outline-none focus:border-brand dark:border-slate-800 dark:bg-slate-900 dark:text-slate-100"
                  autoFocus
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-ink dark:text-slate-200 mb-1">
                  {t('flow.triggerLabel')} <span className="text-rose-500">*</span>
                </label>
                <input
                  value={editing.trigger}
                  onChange={(e) => setEditing({ ...editing, trigger: e.target.value })}
                  placeholder="e.g. feedback, book, apply"
                  className="w-full rounded-xl border border-line bg-white px-3.5 py-2 text-sm text-ink outline-none focus:border-brand dark:border-slate-800 dark:bg-slate-900 dark:text-slate-100"
                />
              </div>
            </div>

            {/* Status toggle */}
            <div className="flex items-center justify-between rounded-xl border border-line bg-surface p-3 dark:border-slate-800 dark:bg-slate-900">
              <div>
                <span className="block text-xs font-bold text-ink dark:text-slate-200">
                  {t('forms.enabled')}
                </span>
                <span className="text-[11px] text-ink-3 dark:text-slate-400">
                  {t('flow.enabledHelp')}
                </span>
              </div>
              <Toggle
                on={editing.enabled}
                onChange={(v) => setEditing({ ...editing, enabled: v })}
                label={t('forms.enabled')}
              />
            </div>

            {/* Questions Builder */}
            <div className="space-y-3 pt-2">
              <div className="flex items-center justify-between">
                <div>
                  <span className="text-xs font-bold text-ink dark:text-slate-200">
                    {t('flow.questions')} ({editing.questions.length})
                  </span>
                  <p className="text-[11px] text-ink-3 dark:text-slate-400">
                    {t('flow.questionsHelp')}
                  </p>
                </div>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() =>
                    setEditing({
                      ...editing,
                      questions: [...editing.questions, newQ()],
                    })
                  }
                  disabled={editing.questions.length >= 20}
                  className="text-xs"
                >
                  <PlusIcon size={12} />
                  <span>{t('flow.addQ')}</span>
                </Button>
              </div>

              <div className="space-y-2.5">
                {editing.questions.map((q, i) => (
                  <div
                    key={q.id}
                    className="rounded-xl border border-line bg-white p-3 shadow-sm dark:border-slate-800 dark:bg-slate-900"
                  >
                    <div className="mb-2 flex items-center justify-between text-xs font-semibold text-ink-3 dark:text-slate-400">
                      <span className="flex items-center gap-1.5">
                        <span className="w-5 h-5 rounded-full bg-brand/10 text-brand text-[11px] font-bold grid place-items-center">
                          {i + 1}
                        </span>
                        <span>{t('flow.questionNum')} {i + 1}</span>
                      </span>
                      {editing.questions.length > 1 && (
                        <button
                          type="button"
                          onClick={() =>
                            setEditing({
                              ...editing,
                              questions: editing.questions.filter((_, x) => x !== i),
                            })
                          }
                          className="text-rose-500 hover:text-rose-700 text-xs font-bold px-1.5 py-0.5"
                        >
                          ✕
                        </button>
                      )}
                    </div>

                    <div className="space-y-2">
                      <div>
                        <label className="block text-[11px] text-ink-3 dark:text-slate-400 mb-0.5">
                          {t('flow.prompt')} <span className="text-rose-500">*</span>
                        </label>
                        <input
                          value={q.prompt}
                          onChange={(e) => patchQ(i, { prompt: e.target.value })}
                          placeholder={t('flow.promptPlaceholder')}
                          className="w-full rounded-lg border border-line bg-surface px-2.5 py-1.5 text-xs text-ink outline-none focus:border-brand dark:border-slate-800 dark:bg-slate-800 dark:text-slate-100"
                        />
                      </div>

                      <div>
                        <label className="block text-[11px] text-ink-3 dark:text-slate-400 mb-0.5">
                          {t('flow.fieldKey')} <span className="text-rose-500">*</span>
                        </label>
                        <input
                          value={q.fieldKey}
                          onChange={(e) =>
                            patchQ(i, {
                              fieldKey: e.target.value
                                .toLowerCase()
                                .replace(/\s+/g, '_')
                                .replace(/[^a-z0-9_]/g, ''),
                            })
                          }
                          placeholder="e.g. feedback_score, user_email"
                          className="w-full rounded-lg border border-line bg-surface px-2.5 py-1.5 text-xs font-mono text-ink outline-none focus:border-brand dark:border-slate-800 dark:bg-slate-800 dark:text-slate-100"
                        />
                        <span className="text-[10px] text-ink-4 dark:text-slate-500">
                          {t('flow.fieldKeyHelp')}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Completion Message */}
            <div>
              <label className="block text-xs font-bold text-ink dark:text-slate-200 mb-1">
                {t('flow.completion')}
              </label>
              <textarea
                rows={2}
                value={editing.completion ?? ''}
                onChange={(e) => setEditing({ ...editing, completion: e.target.value })}
                placeholder={t('flow.completionPlaceholder')}
                className="w-full rounded-xl border border-line bg-white px-3 py-2 text-xs text-ink outline-none focus:border-brand dark:border-slate-800 dark:bg-slate-900 dark:text-slate-100"
              />
              <span className="text-[10px] text-ink-4 dark:text-slate-500">
                {t('flow.completionHelp')}
              </span>
            </div>

            {/* Google Sheets Sync (WP-05) */}
            <div className="rounded-xl border border-line bg-surface p-3.5 space-y-3 dark:border-slate-800 dark:bg-slate-900">
              <div className="flex items-center justify-between">
                <div>
                  <span className="block text-xs font-bold text-ink dark:text-slate-200 flex items-center gap-1.5">
                    <span>📊</span>
                    <span>{t('flow.googleSheetSyncTitle')}</span>
                  </span>
                  <span className="text-[11px] text-ink-3 dark:text-slate-400">
                    {t('flow.googleSheetSyncHelp')}
                  </span>
                </div>
                <Toggle
                  on={useGoogleSheet}
                  onChange={(v) => {
                    setUseGoogleSheet(v);
                    if (v && googleAccounts.length > 0 && !editing.googleAccountId) {
                      setEditing({
                        ...editing,
                        googleAccountId: googleAccounts[0].id,
                      });
                    }
                  }}
                  label={t('flow.googleSheetSyncTitle')}
                />
              </div>

              {useGoogleSheet && (
                <div className="space-y-3 pt-2 border-t border-line dark:border-slate-800">
                  {googleAccounts.length === 0 ? (
                    <div className="p-2.5 rounded-lg border border-amber-200 bg-amber-50 text-[11px] text-amber-800 dark:border-amber-900/50 dark:bg-amber-950/40 dark:text-amber-300">
                      {t('flow.noGoogleAccountHelp')}{' '}
                      <button
                        type="button"
                        onClick={() => navigate('/google-integration')}
                        className="underline font-bold"
                      >
                        {t('flow.connectGoogleNow')}
                      </button>
                    </div>
                  ) : (
                    <>
                      <div>
                        <label className="block text-[11px] font-semibold text-ink-3 dark:text-slate-400 mb-1">
                          {t('flow.selectGoogleAccount')}
                        </label>
                        <select
                          value={editing.googleAccountId || ''}
                          onChange={(e) =>
                            setEditing({
                              ...editing,
                              googleAccountId: e.target.value,
                              googleSheetId: '',
                            })
                          }
                          className="w-full rounded-xl border border-line bg-white px-3 py-1.5 text-xs text-ink outline-none focus:border-brand dark:border-slate-800 dark:bg-slate-800 dark:text-slate-100"
                        >
                          {googleAccounts.map((acc) => (
                            <option key={acc.id} value={acc.id}>
                              {acc.email}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <div className="flex items-center justify-between mb-1">
                          <label className="block text-[11px] font-semibold text-ink-3 dark:text-slate-400">
                            {t('flow.selectSheet')}
                          </label>
                          <button
                            type="button"
                            onClick={() => setManualSheetId(!manualSheetId)}
                            className="text-[10px] text-brand hover:underline font-semibold"
                          >
                            {manualSheetId
                              ? t('flow.chooseFromList')
                              : t('flow.enterManualSheetId')}
                          </button>
                        </div>

                        {manualSheetId ? (
                          <input
                            type="text"
                            value={editing.googleSheetId || ''}
                            onChange={(e) =>
                              setEditing({ ...editing, googleSheetId: e.target.value })
                            }
                            placeholder="e.g. 1BxiMVs0XRA5nFMdKvBdBZjgmUUqptlbs74OgvE2upms"
                            className="w-full rounded-xl border border-line bg-white px-3 py-1.5 text-xs font-mono text-ink outline-none focus:border-brand dark:border-slate-800 dark:bg-slate-800 dark:text-slate-100"
                          />
                        ) : (
                          <select
                            value={editing.googleSheetId || ''}
                            onChange={(e) =>
                              setEditing({ ...editing, googleSheetId: e.target.value })
                            }
                            disabled={loadingGoogle}
                            className="w-full rounded-xl border border-line bg-white px-3 py-1.5 text-xs text-ink outline-none focus:border-brand dark:border-slate-800 dark:bg-slate-800 dark:text-slate-100"
                          >
                            <option value="">— {t('flow.pickSheetPlaceholder')} —</option>
                            {googleSheets.map((sh) => (
                              <option key={sh.id} value={sh.id}>
                                {sh.title}
                              </option>
                            ))}
                          </select>
                        )}
                      </div>
                    </>
                  )}

                  <p className="text-[10px] text-ink-4 dark:text-slate-500">
                    {t('flow.sheetRowFormatNote')}
                  </p>
                </div>
              )}
            </div>

            {/* Outbound Webhook Notice */}
            <div className="p-3 rounded-xl border border-blue-200 bg-blue-50 text-[11px] text-blue-800 dark:border-blue-900/50 dark:bg-blue-950/40 dark:text-blue-300">
              <span className="font-bold">{t('flow.webhookNoticeTitle')}:</span>{' '}
              {t('flow.webhookNoticeDesc')}
            </div>

            {err && (
              <div className="p-3 rounded-xl border border-rose-200 bg-rose-50 text-xs text-rose-700 dark:border-rose-900/50 dark:bg-rose-950/40 dark:text-rose-300">
                {err}
              </div>
            )}

            {/* Footer buttons */}
            <div className="flex justify-end gap-2 pt-3 border-t border-line dark:border-slate-800">
              <Button
                variant="ghost"
                onClick={() => setEditing(null)}
                disabled={busy}
              >
                {t('common.cancel')}
              </Button>
              <Button
                variant="primary"
                onClick={handleSave}
                disabled={
                  busy || !editing.name.trim() || !editing.trigger.trim()
                }
              >
                {busy ? t('common.saving') : t('common.save')}
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
